import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Navigate } from "react-router-dom";
import { useStateContext } from "contexts/auth-context";
import { useModal } from "hooks/use-modal";
import { useSnackbar } from "notistack";
import { IBreadcrumbsAndMenu, IInventorySetting } from "types/global";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import {
  InventorySettingFindUniqueQuery,
  InventorySettingType,
  InventorySettingsByTypeQuery,
  useInventorySettingCreateMutation,
  useInventorySettingDeleteMutation,
  useInventorySettingFindUniqueQuery,
  useInventorySettingUpdateMutation,
  useInventorySettingsByTypeQuery,
} from "generated/general";
import CustomizedBreadcrumbs from "components/Custom/CustomizedBreadcrumbs";
import { Box, Typography } from "@mui/material";
import CustomizedButton from "components/Custom/CustomizedButton";
import InventorySettingModal from "components/UI/Modal/InventorySettingModal";
import InventorySettingTable from "components/Table/Setting/InventorySetting";
import DeleteConfirmation from "components/UI/Modal/DeleteConfirmation";
import { useElementHeight } from "hooks/use-element-height";

type Props = {
  type: "cate_1" | "cate_2" | "cate_3";
};

const CategorySetting = ({ type }: Props) => {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<number>();
  const {
    state: { permissions },
  } = useStateContext();
  const [headerRef, headerHeight] = useElementHeight();

  let [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("id");

  const setSearchParamsHandler = (key: string, value: string) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const removeSearchParamsHandler = (key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  };

  const { modal, openModalHandler, closeModalHandler } = useModal(
    undefined,
    () => removeSearchParamsHandler("id")
  );

  const breadcrumbName =
    t("setting.inventory.category") + " " + type.split("_")[1] + " ";

  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.inventory.index"),
      to: "/setting/inventory",
    },
    {
      name: breadcrumbName,
    },
  ];

  const graphQLClientGeneral: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { data: rowData, refetch: refetchRowData } =
    useInventorySettingsByTypeQuery<InventorySettingsByTypeQuery>(
      graphQLClientGeneral,
      {
        type: type as InventorySettingType,
      }
    );

  const { data, isLoading, isSuccess } =
    useInventorySettingFindUniqueQuery<InventorySettingFindUniqueQuery>(
      graphQLClientGeneral,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: Boolean(id),
      }
    );

  const { mutateAsync: createCategory } =
    useInventorySettingCreateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: updateCategory } =
    useInventorySettingUpdateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: deleteCategory } =
    useInventorySettingDeleteMutation<Error>(graphQLClientGeneral);

  const onAddHandler = async (data: IInventorySetting) => {
    try {
      await createCategory({
        createInput: {
          name: data.name,
          type: type as InventorySettingType,
        },
      });
      enqueueSnackbar(`เพิ่ม${breadcrumbName}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`เพิ่ม${breadcrumbName}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onEditHandler = (id?: number) => {
    if (id) {
      setSearchParamsHandler("id", id.toString());
      openModalHandler();
    }
  };

  const onUpdateHandler = async (data: IInventorySetting) => {
    try {
      const { id, ...otherData } = data;
      await updateCategory({
        uniqueInput: {
          id: id,
        },
        updateInput: {
          name: otherData.name,
          type: type as InventorySettingType,
        },
      });
      enqueueSnackbar(`แก้ไข${breadcrumbName}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`แก้ไข${breadcrumbName}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onDeleteHandler = (id?: number) => {
    if (id) {
      setDeletingId(id);
      openConfirmationHandler();
    }
  };

  const deleteActionHandler = async () => {
    try {
      await deleteCategory({
        uniqueInput: {
          id: deletingId,
        },
      });
      setDeletingId(undefined);
      enqueueSnackbar(`ลบ${breadcrumbName}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`ลบ${breadcrumbName}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const {
    modal: confirmation,
    openModalHandler: openConfirmationHandler,
    closeModalHandler: closeConfirmationHandler,
    submitModalHandler: submitConfirmationHandler,
  } = useModal(deleteActionHandler);

  if (permissions && !permissions.inventory_setting?.view) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <>
      <Box ref={headerRef}>
        <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
        <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
          <Typography variant="h5">{breadcrumbName}</Typography>
          {permissions?.inventory_setting?.create && (
            <CustomizedButton
              title={t("button.add") + breadcrumbName}
              variant="contained"
              onClick={openModalHandler}
              startAddIcon
            />
          )}
        </Box>
      </Box>
      <InventorySettingTable
        data={rowData?.InventorySettingsByType || []}
        onEditHandler={onEditHandler}
        onDeleteHandler={onDeleteHandler}
        height={`calc(90vh - ${headerHeight + 85}px)`}
      />
      <InventorySettingModal
        label={breadcrumbName}
        open={modal}
        closeModalHandler={closeModalHandler}
        onAddHandler={onAddHandler}
        onUpdateHandler={onUpdateHandler}
        data={data?.InventorySettingFindUnique}
        isLoading={isLoading}
        isSuccess={isSuccess}
      />
      <DeleteConfirmation
        title={`ลบ${breadcrumbName}`}
        message={
          <Box>
            <Typography>{`ยืนยันการลบ${t(
              "setting.inventory.category"
            )}`}</Typography>
            <Typography fontWeight="bold">
              {
                rowData?.InventorySettingsByType.find(
                  (setting) => setting.id === deletingId
                )?.name
              }
            </Typography>
          </Box>
        }
        open={confirmation}
        handleClose={closeConfirmationHandler}
        action={submitConfirmationHandler}
      />
    </>
  );
};

export default CategorySetting;
